import React, { useState, useEffect } from 'react'
import { Box, Container, Toolbar, useTheme, useMediaQuery } from '@mui/material'
import { CollapsibleSidebar } from './CollapsibleSidebar'
import { Navigation } from './Navigation'
import { Footer } from './Footer'
import { useAuth } from '@/contexts/AuthContext'

interface SidebarLayoutProps {
  children: React.ReactNode
  showNavigation?: boolean
}

const DRAWER_WIDTH = 240
const MINI_RAIL_WIDTH = 64

export const SidebarLayout: React.FC<SidebarLayoutProps> = ({
  children,
  showNavigation = true,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const { isAuthenticated } = useAuth()

  // Mobile drawer open state (only used on xs–sm)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)

  // Close mobile drawer when breakpoint changes away from mobile
  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerOpen(false)
    }
  }, [isMobile])

  const handleSidebarToggle = (): void => {
    setMobileDrawerOpen(prev => !prev)
  }

  // For non-authenticated users, use simple layout without sidebar
  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {showNavigation && <Navigation />}
        <Toolbar /> {/* Spacer for fixed AppBar */}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Container maxWidth="lg" sx={{ py: 3 }}>
            {children}
          </Container>
        </Box>
        <Footer />
      </Box>
    )
  }

  // Determine left margin for main content based on breakpoint
  // Mobile: no margin (drawer is temporary overlay)
  // Tablet: mini-rail width
  // Desktop: full drawer width
  const contentMarginLeft = isMobile ? 0 : isTablet ? `${MINI_RAIL_WIDTH}px` : `${DRAWER_WIDTH}px`

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top Navigation */}
      {showNavigation && (
        <Navigation
          onSidebarToggle={handleSidebarToggle}
          showSidebarToggle={isAuthenticated}
          position="fixed"
          zIndex={theme.zIndex.appBar}
        />
      )}

      {/* Sidebar */}
      {showNavigation && isAuthenticated && (
        <CollapsibleSidebar
          open={mobileDrawerOpen}
          onToggle={handleSidebarToggle}
        />
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          ml: contentMarginLeft,
          transition: theme.transitions.create('margin-left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Toolbar spacer to prevent content hiding behind fixed AppBar */}
        <Toolbar />

        <Box sx={{ flexGrow: 1, width: '100%' }}>
          <Container
            maxWidth="xl"
            sx={{
              py: 3,
              px: { xs: 2, md: 3 },
              width: '100%',
            }}
          >
            {children}
          </Container>
        </Box>
        <Footer />
      </Box>
    </Box>
  )
}
